// src/utils/mapUtils.js

import leafletPip from '@mapbox/leaflet-pip';
import { kmeans } from 'ml-kmeans';
import L from 'leaflet';

const settings = {
  latMin: 41.0,
  latMax: 51.1,
  lngMin: -5.0,
  lngMax: 9.5,
  locationEdges: {
    distance: 0.08, //in km
    num: 3,
    sideEdgesDiff: 0.000035,
  },
  markerIcon: {
    iconUrl: 'https://www.svgrepo.com/show/302636/map-marker.svg',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  },
  wedge: {
    wideness: 40,
    curvatureFactor: 0.3,
  },
};

export const cellMarkersReprPolySizesPerZoom = {
  20: 25,
  19: 25,
  18: 25,
  17: 25,
  16: 25,
  15: 20,
  14: 10,
  13: 10,
  12: 10,
  11: 10,
  10: 10,
};

export const calculateOrientationShiftedPosition = (
  leafletMapInstance,
  shiftSizePxs,
  initialLocation, // [lat, lgn]
  orientation,
  shiftPositions
  ) => {
  // Convert the center LatLng to pixel coordinates
  const centerPoint = leafletMapInstance.latLngToLayerPoint(initialLocation);
  // Convert orientation from degrees to radians
  const orientationRad = (orientation * Math.PI) / 180;
  // Calculate the shift distance in pixels based on icon size and index
  const shiftDistancePx = (shiftSizePxs / 1.5) *   shiftPositions;
  // Calculate the new pixel coordinates by shifting
  const shiftedX = centerPoint.x + shiftDistancePx * Math.sin(orientationRad); // East/West shift
  const shiftedY = centerPoint.y - shiftDistancePx * Math.cos(orientationRad); // North/South shift
  // Convert the new pixel coordinates back to LatLng
  const shiftedLatLng = leafletMapInstance.layerPointToLatLng(L.point(shiftedX, shiftedY));
  return shiftedLatLng;
};

export const createEdge = (
  orientation,
  lat,
  lng,
  numPoints = 10,
  sides = false,
  distanceLength = settings.locationEdges.distance
) => {
  const angle = (orientation * Math.PI) / 180;

  // Convert the distanceLength from kilometers to degrees for latitude and longitude
  const distanceLat = distanceLength / 111; // 1 degree latitude = ~111 km
  const distanceLng = distanceLength / (111 * Math.cos((lat * Math.PI) / 180)); // Adjust for latitude

  const points = [];
  const sideEdgesDiff = sides ? settings.locationEdges.sideEdgesDiff : 0;

  for (let i = 0; i < numPoints; i++) {
    const t = i / numPoints;
    const pointLat = lat + (t * distanceLat - sideEdgesDiff) * Math.cos(angle);
    const pointLng = lng + (t * distanceLng - sideEdgesDiff) * Math.sin(angle);
    points.push([pointLat, pointLng]);
  }

  return points; // This returns an array of points
};

export const splitLine = (startLat, startLng, endLat, endLng, numPoints = 10) => {
  const points = [];

  // Calculate differences in latitude and longitude
  const latDiff = (endLat - startLat) / (numPoints - 1);
  const lngDiff = (endLng - startLng) / (numPoints - 1);

  // Generate intermediate points
  for (let i = 0; i < numPoints; i++) {
    const pointLat = startLat + i * latDiff;
    const pointLng = startLng + i * lngDiff;
    points.push({'lat': pointLat, 'lon': pointLng});
  }

  return points;
};

export const groupMarkersFromData = (bandsList, markersDataArray) => {

  const sitesMarkersInfoMap = new Map(); // Use Map for faster lookup
  const sitesMarkersInfo = [];

  // Process markers
  for (const markerData of markersDataArray) {
   const orientation = markerData.orientation || 0; // Group by orientation within location

   let lat = 0;
   let lng = 0;
   if (
     markerData.location?.lat === undefined ||
     markerData.location?.lng === undefined
   ) {
     console.warn(
       `Latitude or Longitude is undefined for marker with id ${markerData.id}`
     );
     continue;
   } else {
     lat = markerData.location?.lat;
     lng = markerData.location?.lng;
   }

    // Create a unique key for the location
    const locationKey = `${lat},${lng}`;

    // Find or create the group for this location
    let siteMarkerInfo = sitesMarkersInfoMap.get(locationKey);
    if (!siteMarkerInfo) {
      siteMarkerInfo = {
        searchKey: '',
        sites: [],
        location: { lat, lng },
        orientations: new Map(), // Use Map for orientation groups
      };
      sitesMarkersInfoMap.set(locationKey, siteMarkerInfo);
      sitesMarkersInfo.push(siteMarkerInfo);
    }
    // now see if the concrete site was added to the siteMarkerInfo.sites array
    // this is to add in the same siteMarkerInfo (lat/long) the
    // shared site for different techs
    if(!siteMarkerInfo.sites.find((site) => 
      site["SITE_NATURAL_KEY"] === markerData["SITE_NATURAL_KEY"]
      && site["TECHNOLOGY"] === markerData["TECHNOLOGY"]
    )){
      siteMarkerInfo.searchKey = siteMarkerInfo.searchKey + markerData.contextInfo["SITE NAME"] + ' ';
      siteMarkerInfo.sites.push({...markerData.contextInfo});
    }

    // Find or create the group for this orientation within the location group
    let orientationMarkerInfo = siteMarkerInfo.orientations.get(orientation);
    if (!orientationMarkerInfo) {
      orientationMarkerInfo = {
        orientation: orientation,
        markers: [],
      };
      siteMarkerInfo.orientations.set(orientation, orientationMarkerInfo);
    }

    // Add the marker to the appropriate orientation group
    if (markerData.contextInfo['CELL_BAND_NAME']) {

      // calculate Band informatino for the cell MArker info
      const bandFilterId = markerData.contextInfo['CELL_BAND_NAME'];
      const bandFilterItem = bandsList.find(
        (bandItem) => bandItem.id === bandFilterId
      );

      if (bandFilterItem) {
        // prepare the search key for the search functionality
        // make the site to have all the cells to make the match at the root siteMarkerInfo level
        siteMarkerInfo.searchKey =
          siteMarkerInfo.searchKey + markerData.contextInfo['CELL Name'] + ' ';

        // band is found then add the cell to the orientation array
        const cellMarkerInfo = {
          ...markerData,
          ...markerData.contextInfo,
          bandFilterItemId: bandFilterItem.id,
          cellBandColor: bandFilterItem.contextInfo["CELL_BAND_NAME_COLOR"],
          orientation: orientationMarkerInfo.orientation,
          cellIndexWithinOrientation: orientationMarkerInfo.markers.length + 1,
        };

        orientationMarkerInfo.markers.push(cellMarkerInfo);
      }
    }
  }

  return sitesMarkersInfo;
};


export const clusterMarkers = (markers, numClusters) => {
  const data = markers.map((marker) => [
    marker.location[0],
    marker.location[1],
  ]);
  const clusters = kmeans(data, numClusters);
  return markers.map((marker, index) => ({
    ...marker,
    cluster: clusters.clusters[index],
  }));
};

export const generatePerpendicularLines = (edge) => {
  const [lat1, lng1, lat2, lng2] = edge;
  const numLines = 4;
  const lines = [];
  const dx = (lat2 - lat1) / (numLines + 1);
  const dy = (lng2 - lng1) / (numLines + 1);

  for (let i = 1; i <= numLines; i++) {
    const px = lat1 + dx * i;
    const py = lng1 + dy * i;
    const halfPerpendicularLength = 0.0001;

    const perpDx = dy;
    const perpDy = -dx;
    const length = Math.sqrt(perpDx * perpDx + perpDy * perpDy);
    const unitPerpDx = (perpDx / length) * halfPerpendicularLength;
    const unitPerpDy = (perpDy / length) * halfPerpendicularLength;

    const perpLat1 = px + unitPerpDx;
    const perpLng1 = py + unitPerpDy;
    const perpLat2 = px - unitPerpDx;
    const perpLng2 = py - unitPerpDy;

    const type = `type_${(i % 3) + 1}`;

    lines.push({
      line: [
        [perpLat1, perpLng1],
        [perpLat2, perpLng2],
      ],
      type,
    });
  }
  return lines;
};

export const checkMarkersInsidePolygon = (layer, markers) => {
  const polygon = layer.toGeoJSON();

  const markersInside = markers.filter((marker) => {
    const point = L.latLng(marker.location);
    const isInside = leafletPip.pointInLayer(point, L.geoJSON(polygon));
    return isInside.length > 0;
  });
  return markersInside;
};

export const fetchElevationOE = async (points) => {
  const pointsObject = points.map((p) => `${p.lat},${p.lon}`).join('|');
  const url = `https://api.open-elevation.com/api/v1/lookup?locations=${pointsObject}`;
  const response = await fetch(url);
  const data = await response.json();
  if(data && data.results) {
    return data.results.map((result) => ({
      elevation: result.elevation,
      lat: result.latitude,
      lon: result.longitude
    }));
  }
  return null;
};

export const generateBingStyleString = (styleObj) => {
  function cleanColor(color) {
      return color.replace(/^#/, ''); // Remove the '#' from hex color
  }

  return [
      styleObj.mapElement?.labelVisible === false ? 'me|lv:0' : '',
      styleObj.area?.visible === false ? 'ar|v:0' : '',
      styleObj.transportation
          ? `trs|loc:${cleanColor(styleObj.transportation.labelOutlineColor)};fc:${cleanColor(styleObj.transportation.fillColor)};sc:${cleanColor(styleObj.transportation.strokeColor)}`
          : '',
      styleObj.countryRegion?.visible === false ? 'cr|v:0;bv:0' : '',
      styleObj.adminDistrict?.visible === false ? 'ad|v:0;bv:0' : '',
      styleObj.political?.visible === false ? 'pl|bv:0;v:0' : '',
      styleObj.water?.fillColor ? `wt|fc:${cleanColor(styleObj.water.fillColor)}` : '',
      styleObj.point?.visible === false ? 'pt|v:0' : ''
  ]
  .filter(Boolean) // Remove empty values
  .join('_'); // Use `_` as separator
};

export const bingLayerOptions = {
  imagerySet: 'RoadOnDemand',
  style: generateBingStyleString({
    mapElement: {
        labelVisible: false
    },
    area: {
        visible: false
    },
    transportation: {
        labelOutlineColor: "#efeeea",
        fillColor: "#efeeea",
        strokeColor: "#efeeea",
        visible: false
    },
    countryRegion: {
        visible: false,
        borderVisible: false
    },
    adminDistrict: {
        visible: false,
        borderVisible: false
    },
    political: { borderVisible: false, visible: false },
    water: {
        fillColor: "#efeeea"
    },
    point: {
        visible: false
    },
  })
}; 

export const fetchElevation = async (points) => {
  const pointsObject = points.map((p) => `${p.lat},${p.lon}`).join(',');
  const apiKey =
    'AuHHgIR5cpfytNpr7-VoDK54mW0Mz-PC2GYF0wog6kqbKszZmTq0mve0dZaw3JSm';
  const url = `https://dev.virtualearth.net/REST/v1/Elevation/List?points=${pointsObject}&key=${apiKey}`;

  const response = await fetch(url);
  const data = await response.json();
  if (data.resourceSets && data.resourceSets[0].resources) {
    points.map((point, index) => {
      point.elevation = data.resourceSets[0].resources[0].elevations[index];
    });
    return points;
  }
  return null;
};

export const extractCells = (data) => {
  let cells = [];
  data.forEach((site) => {
    site.orientations.forEach((orientation) => {
      orientation.markers.forEach((marker) => {
        let cellFromMarker = {
          ...marker.contextInfo,
          orientation: orientation.orientation,
          site: site.name,
          name: marker.name,
          id: marker.id,
        };
        cells = cells.concat(cellFromMarker);
      });
    });
  });
  return cells;
};

export const interpolateHeights = async (points) => {
  const elevation = await fetchElevation(points);
  return elevation;
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in kilometers
};

export const createTiltCoordinates = (points, tilt, initialAltitude) => {
  const tiltCoordinates = [];
  const initialPoint = points[0];
  const finalPoint = points[points.length - 1];

  // Calculate total horizontal distance in terms of longitude (simplified)
  const totalDistance = Math.sqrt(
    Math.pow(finalPoint.lat - initialPoint.lat, 2) +
      Math.pow(finalPoint.lon - initialPoint.lon, 2)
  );

  // Calculate the horizontal distance scale assuming Earth's circumference along the equator
  const earthCircumference = 40075017; // meters around the equator
  const distanceScale = (totalDistance / 360) * earthCircumference;

  // Convert tilt angle from degrees to radians
  const tiltRadians = (tilt * Math.PI) / 180;

  for (let i = 0; i < points.length; i++) {
    const t = i / (points.length - 1);
    const lat = initialPoint.lat + t * (finalPoint.lat - initialPoint.lat);
    const lon = initialPoint.lon + t * (finalPoint.lon - initialPoint.lon);
    const horizontalDisplacement = t * distanceScale;
    const elevationChange = horizontalDisplacement * Math.tan(tiltRadians);
    const elevation = initialAltitude + elevationChange;

    tiltCoordinates.push([lon, lat, elevation < 0 ? 0 : elevation]);
  }

  return tiltCoordinates;
};

export const createFeatureCollection = async (points, antennaHeight, tilt) => {
  const featureCollection = [];
  let initialAltitude = 0;

  // Base height graph
  const heights = await interpolateHeights(points);
  initialAltitude = heights[0].elevation + antennaHeight;
  console.log(heights);

  // Step 2: Create the tilted line
  let tiltCoordinatesObject = createTiltCoordinates(
    points,
    tilt,
    initialAltitude
  );

  // Step 3: Calculate accurate distanceSteps using Haversine formula
  let totalDistance = 0;
  let distanceSteps = [0]; // Start with the first point having distance 0

  for (let i = 1; i < points.length; i++) {
    const prevPoint = tiltCoordinatesObject[i - 1];
    const currentPoint = tiltCoordinatesObject[i];

    console.log(tiltCoordinatesObject);
    console.log(prevPoint);
    console.log(currentPoint);

    const segmentDistance = haversineDistance(
      prevPoint[1],
      prevPoint[0], // Latitude and Longitude of the previous point
      currentPoint[1],
      currentPoint[0] // Latitude and Longitude of the current point
    );

    totalDistance += segmentDistance;
    distanceSteps.push((totalDistance * 1000).toFixed()); // Convert kilometers to meters
  }

  // Return the results
  return {
    linesData: tiltCoordinatesObject.map((coord) => coord[2]),
    earthElevationData: heights.map((coord) => coord.elevation),
    points,
    distanceSteps,
  };
};
